import { useHasAnyAdminPermission } from '@treneo/features/admin';
import { ChangePasswordFormContainer, performLogout } from '@treneo/features/auth';
import { useSelectCurrentUserInfo, useSelectUserDisplayName } from '@treneo/features/user';
import { AvatarConsts, urls } from '@treneo/shared/constants';
import { useAppTranslations } from '@treneo/shared/i18n';
import { AccountCircleIcon, LockIcon, LogoutIcon, SettingsIcon } from '@treneo/shared/icons';
import { useAppLocation, useAppNavigate } from '@treneo/shared/router';
import { Colors, Spacing } from '@treneo/shared/themes';
import { ActionPosition, BaseModal, Box, styled, Typography, UserAvatar } from '@treneo/shared/ui';
import { useBoolean } from '@treneo/shared/utils';

import { MyAccountFormContainer } from './my-account-form-container';
import { MyAccountModal } from './my-account-modal';
import { StyledWrapper } from './user-profile-modal.styles';

const Options = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: ${Spacing.small};
`;

const Container = styled(Box)`
  position: relative;
`;

const TypographyDisplayName = styled(Typography)`
  text-wrap: nowrap;
`;

export const UserProfileModal = () => {
  const { t } = useAppTranslations();
  const navigate = useAppNavigate();
  const location = useAppLocation();
  const [menuIsDisplayed, showMenu, hideMenu] = useBoolean();
  const [myAccountDisplayed, displayMyAccount, hideMyAccount] = useBoolean();
  const [changePasswordDisplayed, displayChangePassword, hideChangePassword] = useBoolean();
  const hasAnyAdminPermission = useHasAnyAdminPermission();
  const isInAdminPanel = location.pathname.startsWith('/admin');

  const currentUserInfo = useSelectCurrentUserInfo();
  const currentUserInfoDisplayName = useSelectUserDisplayName() ?? AvatarConsts.fallbackUserName;

  const handleLogoutClick = () => {
    void performLogout();
  };

  const handleDisplayMyAccount = () => {
    hideMenu();
    displayMyAccount();
  };

  const handleDisplayChangePassword = () => {
    hideMenu();
    displayChangePassword();
  };

  const handleNavigateToAdminPanel = () => {
    hideMenu();
    navigate(urls.adminPanel);
  };

  return (
    <Container>
      <StyledWrapper onClick={showMenu}>
        <TypographyDisplayName>{currentUserInfoDisplayName}</TypographyDisplayName>
        <UserAvatar
          displayName={currentUserInfoDisplayName}
          color={currentUserInfo?.avatarColor ?? ''}
          avatarUrl={currentUserInfo?.avatar}
        />
      </StyledWrapper>

      {menuIsDisplayed && (
        <BaseModal open={true} onClose={hideMenu} variant='dropdown'>
          <Options>
            {hasAnyAdminPermission && !isInAdminPanel && (
              <ActionPosition
                icon={<SettingsIcon style={{ color: Colors.green }} />}
                text={t('userMenu.adminPanel')}
                onClick={handleNavigateToAdminPanel}
              />
            )}
            <ActionPosition
              icon={<AccountCircleIcon style={{ color: Colors.green }} />}
              text={t('userMenu.myAccount')}
              onClick={handleDisplayMyAccount}
            />
            <ActionPosition
              icon={<LockIcon style={{ color: Colors.green }} />}
              text={t('userMenu.changePassword')}
              onClick={handleDisplayChangePassword}
            />
            <ActionPosition
              icon={<LogoutIcon style={{ color: Colors.green }} />}
              text={t('userMenu.logOut')}
              onClick={handleLogoutClick}
            />
          </Options>
        </BaseModal>
      )}

      {myAccountDisplayed && (
        <MyAccountModal open={myAccountDisplayed} onClose={hideMyAccount}>
          <MyAccountFormContainer onClose={hideMyAccount} />
        </MyAccountModal>
      )}

      {changePasswordDisplayed && (
        <BaseModal
          open={true}
          onClose={hideChangePassword}
          title={t('userMenu.changePassword')}
          variant='positioned'
          maxWidth='600px'
        >
          <ChangePasswordFormContainer onClose={hideChangePassword} />
        </BaseModal>
      )}
    </Container>
  );
};
